import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { Empty, SiteHead, featureLabel, formatDate, incidentStateLabel, severityLabel } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface StatusPayload {
  components: { component: string; state: string; measured_at: string | null; stale: boolean }[];
  incidents: {
    reference: string; title: string; severity: string; state: string;
    started_at: string | null; resolved_at: string | null; affected_games: string[];
  }[];
  note: string;
  updated_at: string;
}

const COMPONENT_LABEL: Record<string, string> = {
  notices_api: '공지 전달',
  rewards_api: '보상 지급',
  purchase_api: '구매 검증',
};

const STATE_LABEL: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' }> = {
  operational: { label: '정상', tone: 'ok' },
  degraded: { label: '지연', tone: 'warn' },
  outage: { label: '장애', tone: 'danger' },
  unknown: { label: '판정 불가', tone: 'neutral' },
  no_recent_measurement: { label: '최근 측정 없음', tone: 'neutral' },
};

const SEVERITY_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  minor: 'warn',
  major: 'danger',
  critical: 'danger',
};

/** P012 — service status. Missing measurements are shown as missing, not as green. */
export function PublicStatus() {
  const status = useResource<StatusPayload>(
    (signal) => api.public('/api/v1/status', { signal }) as Promise<ApiEnvelope<StatusPayload>>,
    [],
    { emptyWhen: () => false },
  );

  const data = status.data;
  const ongoing = (data?.incidents ?? []).filter((incident) => !incident.resolved_at);
  const resolved = (data?.incidents ?? []).filter((incident) => incident.resolved_at);

  return (
    <>
      <SiteHead
        eyebrow="서비스 상태"
        title="서비스 상태"
        lead="공지 전달·보상 지급·구매 검증의 최근 상태를 확인해 보세요."
        meta={data ? <span>기준 {formatDate(data.updated_at, true)}</span> : undefined}
      />

      <StateCard state={status.state} error={status.error} onRetry={status.reload}>
        {data && (
          <>
            {ongoing.length > 0 && (
              <div className="banner" style={{ marginBottom: 28 }}>
                <span aria-hidden="true">!</span>
                <div>진행 중인 장애 {ongoing.length}건</div>
              </div>
            )}

            <section className="card" style={{ marginBottom: 32 }}>
              {data.components.map((component) => {
                const info = STATE_LABEL[component.state] ?? STATE_LABEL.unknown;
                return (
                  <div key={component.component} className="status-row">
                    <span className="status-row__name">{COMPONENT_LABEL[component.component] ?? component.component}</span>
                    <span className="status-row__meta">
                      {component.measured_at ? formatDate(component.measured_at, true) : '측정 기록 없음'}
                    </span>
                    <Pill tone={info.tone}>{info.label}</Pill>
                  </div>
                );
              })}
            </section>

            <div className="banner banner--info" style={{ marginBottom: 32 }}>
              <span aria-hidden="true">ℹ</span>
              <div>{data.note}</div>
            </div>

            <section style={{ marginBottom: 32 }}>
              <h2 className="section__title" style={{ marginBottom: 12 }}>진행 중인 장애</h2>
              {ongoing.length === 0 ? (
                <Empty title="진행 중인 장애가 없어요" body="현재 신고된 장애가 없어요." />
              ) : (
                <div className="row-list">
                  {ongoing.map((incident) => (
                    <Link key={incident.reference} to={`/status/incidents/${incident.reference}`} className="row">
                      <div className="row__main">
                        <h3 className="row__title">{incident.title}</h3>
                        <p className="row__excerpt">
                          {incident.affected_games.length > 0 ? `영향: ${incident.affected_games.join(', ')}` : '영향 범위 확인 중'}
                        </p>
                      </div>
                      <div className="row__aside">
                        <Pill tone={SEVERITY_TONE[incident.severity] ?? 'neutral'}>{severityLabel(incident.severity)}</Pill>
                        <span>{formatDate(incident.started_at, true)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="section__title" style={{ marginBottom: 12 }}>최근 7일 기록</h2>
              {resolved.length === 0 ? (
                <Empty title="최근 7일간 종료된 장애가 없어요" />
              ) : (
                <div className="row-list">
                  {resolved.map((incident) => (
                    <Link key={incident.reference} to={`/status/incidents/${incident.reference}`} className="row">
                      <div className="row__main"><h3 className="row__title">{incident.title}</h3></div>
                      <div className="row__aside">
                        <Pill tone="ok">해결</Pill>
                        <span>{formatDate(incident.resolved_at, true)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </StateCard>
    </>
  );
}

interface IncidentPayload {
  reference: string; title: string; severity: string; state: string;
  affected_games: string[]; affected_features: string[]; workaround: string | null;
  started_at: string | null; resolved_at: string | null;
  updates: { state: string; body: string; published_at: string | null }[];
}

/** P013 — incident detail. */
export function PublicIncident() {
  const { incidentId } = useParams();

  const incident = useResource<IncidentPayload>(
    (signal) => api.public(`/api/v1/status/incidents/${incidentId}`, { signal }) as Promise<ApiEnvelope<IncidentPayload>>,
    [incidentId],
    { emptyWhen: () => false },
  );

  const data = incident.data;

  return (
    <StateCard state={incident.state} error={incident.error} onRetry={incident.reload}>
      {data && (
        <article className="article">
          <nav className="breadcrumb" style={{ marginBottom: 20 }}>
            <Link to="/status">서비스 상태</Link> / {data.reference}
          </nav>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Pill tone={SEVERITY_TONE[data.severity] ?? 'neutral'}>{severityLabel(data.severity)}</Pill>
            <Pill tone={data.resolved_at ? 'ok' : 'warn'}>{data.resolved_at ? '해결' : incidentStateLabel(data.state)}</Pill>
          </div>

          <h1 className="site-head__title">{data.title}</h1>

          <dl className="spec-list" style={{ marginBottom: 28 }}>
            <dt>시작</dt><dd>{formatDate(data.started_at, true)}</dd>
            <dt>종료</dt><dd>{data.resolved_at ? formatDate(data.resolved_at, true) : '진행 중'}</dd>
            <dt>영향 게임</dt><dd>{data.affected_games.length > 0 ? data.affected_games.join(', ') : '기록 없음'}</dd>
            <dt>영향 기능</dt><dd>{data.affected_features.length > 0 ? data.affected_features.map(featureLabel).join(', ') : '기록 없음'}</dd>
          </dl>

          {data.workaround && (
            <div className="banner banner--info" style={{ marginBottom: 28 }}>
              <span aria-hidden="true">ℹ</span>
              <div><strong>임시 조치</strong><br />{data.workaround}</div>
            </div>
          )}

          <h2 className="section__title" style={{ marginBottom: 12 }}>경과</h2>
          {data.updates.length === 0 ? (
            <Empty title="아직 진행 상황이 등록되지 않았어요" body="새 진행 상황이 등록되면 이곳에서 확인할 수 있어요." />
          ) : (
            <div className="timeline">
              {data.updates.map((update, index) => (
                <div key={index} className="timeline__item">
                  <div className="timeline__meta">
                    <strong>{incidentStateLabel(update.state)}</strong>
                    <span>{formatDate(update.published_at, true)}</span>
                  </div>
                  <p className="timeline__body">{update.body}</p>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 40 }}>
            <Link className="btn" to="/status">← 서비스 상태</Link>
          </div>
        </article>
      )}
    </StateCard>
  );
}
